/**
 * Drag & drop photo picker (Phase 1).
 * Sélection locale → preview → upload différé au submit du post parent.
 */
import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { motion } from "framer-motion";
import { Image as ImageIcon, X, Upload } from "lucide-react";

const MAX_PHOTOS = 4;
const MAX_BYTES = 5 * 1024 * 1024;

export default function PhotoUploader({ photos = [], onChange, disabled }) {
  const onDrop = useCallback(
    (accepted, rejected) => {
      if (rejected?.length) {
        // Notification déjà gérée par le parent si besoin
      }
      const slotsLeft = MAX_PHOTOS - photos.length;
      const next = [...photos, ...accepted.slice(0, slotsLeft).map((f) => ({
        file: f,
        preview: URL.createObjectURL(f),
        name: f.name,
        size: f.size,
      }))];
      onChange(next);
    },
    [photos, onChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
      "image/webp": [".webp"],
    },
    maxSize: MAX_BYTES,
    multiple: true,
    disabled: disabled || photos.length >= MAX_PHOTOS,
  });

  const remove = (idx) => {
    const p = photos[idx];
    if (p.preview) URL.revokeObjectURL(p.preview);
    onChange(photos.filter((_, i) => i !== idx));
  };

  return (
    <div data-testid="photo-uploader">
      {photos.length < MAX_PHOTOS && (
        <div
          {...getRootProps()}
          data-testid="photo-dropzone"
          className="rounded-xl text-center cursor-pointer transition-colors"
          style={{
            border: `2px dashed ${isDragActive ? "#F77F00" : "#E5E5E5"}`,
            background: isDragActive ? "#FFF7ED" : "#FAFAFA",
            padding: "24px 16px",
          }}
        >
          <input {...getInputProps()} />
          <Upload size={20} color={isDragActive ? "#F77F00" : "#666"} className="mx-auto mb-2" />
          <div className="text-sm font-medium" style={{ color: isDragActive ? "#F77F00" : "#1A1A1A" }}>
            {isDragActive ? "Déposez ici…" : "Glissez vos photos ou cliquez"}
          </div>
          <div className="text-xs mt-1" style={{ color: "#999" }}>
            JPG, PNG, WebP · 5 MB max · {MAX_PHOTOS - photos.length}{" "}
            emplacement{MAX_PHOTOS - photos.length > 1 ? "s" : ""} restant{MAX_PHOTOS - photos.length > 1 ? "s" : ""}
          </div>
        </div>
      )}

      {photos.length > 0 && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {photos.map((p, i) => (
            <motion.div
              key={p.preview || p.public_url}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="relative aspect-square rounded-lg overflow-hidden group"
              style={{ border: "1px solid #E5E5E5" }}
              data-testid={`photo-preview-${i}`}
            >
              <img
                src={p.preview || p.public_url}
                alt=""
                className="w-full h-full object-cover"
              />
              <button
                onClick={() => remove(i)}
                disabled={disabled}
                data-testid={`photo-remove-${i}`}
                className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-white/90 hover:bg-red-50 flex items-center justify-center transition-colors"
                style={{ border: "1px solid #E5E5E5" }}
                title="Retirer"
              >
                <X size={12} color="#EF4444" />
              </button>
              <div
                className="absolute bottom-0 left-0 right-0 text-[10px] text-white px-2 py-1"
                style={{ background: "linear-gradient(to top, rgba(0,0,0,0.7), transparent)" }}
              >
                {p.size ? `${(p.size / 1024 / 1024).toFixed(1)} MB` : ""}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
